// DocuMind engine — PDF/text extraction + chunked keyword retrieval.

export interface DocChunk {
  id: string;
  docId: string;
  docName: string;
  page: number;
  text: string;
}

export interface Document {
  id: string;
  name: string;
  type: 'pdf' | 'txt' | 'md';
  chunks: DocChunk[];
  totalChars: number;
  addedISO: string;
}

export async function extractTextFromPdf(file: File): Promise<{ pageTexts: string[]; totalChars: number }> {
  // Lazy-load pdfjs from a CDN at runtime so we don't bloat the bundle.
  const pdfjs: any = await import(/* @vite-ignore */ 'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.7.76/build/pdf.min.mjs');
  pdfjs.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.7.76/build/pdf.worker.min.mjs';
  const buf = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buf }).promise;
  const pageTexts: string[] = [];
  let totalChars = 0;
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const tc = await page.getTextContent();
    const text = tc.items.map((it: any) => it.str).join(' ');
    pageTexts.push(text);
    totalChars += text.length;
  }
  return { pageTexts, totalChars };
}

export function chunkPageText(pageText: string, page: number, docId: string, docName: string, chunkSize = 1500): DocChunk[] {
  const out: DocChunk[] = [];
  const sentences = pageText.split(/(?<=[.!?])\s+/);
  let current = '';
  for (const s of sentences) {
    if (current.length + s.length > chunkSize && current.length > 0) {
      out.push({ id: crypto.randomUUID(), docId, docName, page, text: current.trim() });
      current = s;
    } else {
      current = current ? `${current} ${s}` : s;
    }
  }
  if (current.trim().length > 0) out.push({ id: crypto.randomUUID(), docId, docName, page, text: current.trim() });
  return out;
}

const STOPWORDS = new Set(['the','a','an','is','are','was','were','be','been','being','have','has','had','do','does','did','to','of','for','in','on','at','by','with','about','against','between','into','through','from','up','down','out','over','under','further','then','once','here','there','when','where','why','how','all','any','both','each','few','more','most','other','some','such','no','nor','not','only','own','same','than','too','very','can','will','just','should','now','it','this','that','these','those','i','you','he','she','we','they']);

function tokenize(text: string): string[] {
  return text.toLowerCase().match(/\b[a-z][a-z'-]+\b/g)?.filter(t => !STOPWORDS.has(t) && t.length > 2) || [];
}

// Simple TF-IDF-ish ranking: count query token hits in chunk.
export function retrieve(chunks: DocChunk[], query: string, topK: number = 6): DocChunk[] {
  const qt = tokenize(query);
  if (qt.length === 0) return [];
  const qSet = new Set(qt);
  const scored = chunks.map(c => {
    const tokens = tokenize(c.text);
    const hits = tokens.filter(t => qSet.has(t)).length;
    return { c, score: hits / Math.max(1, tokens.length) * (hits > 0 ? 1 : 0) + hits * 0.01 };
  }).filter(x => x.score > 0).sort((a, b) => b.score - a.score).slice(0, topK);
  return scored.map(x => x.c);
}

export function buildCitedContext(chunks: DocChunk[]): string {
  return chunks.map((c, i) => `[${i + 1}] (${c.docName}, p${c.page})\n${c.text}`).join('\n\n');
}
